import { useState, useEffect, useCallback, useRef } from "react";
import { RefreshCw, Loader2, LayoutGrid, Maximize2, Activity, BarChart3, Sparkles } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { widgetsApi } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

function WidgetCard({ widget, result, loading, onRefresh }) {
  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full text-slate-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-xs">Loading...</span>
        </div>
      );
    }
    if (!result || !result.success) {
      return (
        <div className="flex items-center justify-center h-full text-slate-400 text-xs text-center px-3">
          {result?.error || "Click refresh to load data"}
        </div>
      );
    }

    const data = result.data;

    // KPI widget
    if (widget.type === "kpi" || (!Array.isArray(data) && data)) {
      const kpi = Array.isArray(data) ? null : data;
      if (kpi) {
        return (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-4xl font-bold text-slate-900">{(kpi.value ?? 0).toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-1">{kpi.label}</div>
            {kpi.sub && <div className="text-xs text-slate-400 mt-0.5">{kpi.sub}</div>}
          </div>
        );
      }
    }

    if (!Array.isArray(data) || data.length === 0) {
      return <div className="flex items-center justify-center h-full text-slate-400 text-xs">No data</div>;
    }

    const keys = Object.keys(data[0]);
    const xKey = keys[0];
    const yKeys = keys.slice(1);
    const nameKey = "name" in data[0] ? "name" : xKey;
    const valKey = "value" in data[0] ? "value" : (yKeys[0] || xKey);

    if (widget.type === "pie") {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey={valKey} nameKey={nameKey} cx="50%" cy="50%" outerRadius="55%"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (widget.type === "table") {
      return (
        <div className="overflow-auto h-full">
          <div className="bg-slate-50 sticky top-0 flex border-b border-slate-100">
            {keys.map(k => <div key={k} className="flex-1 min-w-0 px-1.5 py-1 text-left text-slate-500 text-[10px] uppercase">{k}</div>)}
          </div>
          {data.slice(0, 10).map((row, i) => (
            <div key={i} className="flex border-b border-slate-100 hover:bg-slate-50">
              {keys.map(k => <div key={k} className="flex-1 min-w-0 px-1.5 py-1 text-slate-600 text-xs truncate">{String(row[k] ?? "")}</div>)}
            </div>
          ))}
        </div>
      );
    }

    if (widget.type === "bar") {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey={xKey} tick={{ fontSize: 9 }} />
            <YAxis tick={{ fontSize: 9 }} />
            <Tooltip />
            {(yKeys.length > 0 ? yKeys : [valKey]).map((k, i) => <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} />)}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // Default: line/area
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
          <defs>
            {(yKeys.length > 0 ? yKeys : [valKey]).map((k, i) => (
              <linearGradient key={k} id={`lg-${widget.id}-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.15} />
                <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 9 }} />
          <YAxis tick={{ fontSize: 9 }} />
          <Tooltip />
          {(yKeys.length > 0 ? yKeys : [valKey]).map((k, i) => (
            <Area key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]}
              fill={`url(#lg-${widget.id}-${i})`} strokeWidth={2} dot={false} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  const isKpi = widget.type === "kpi" || (result?.success && !Array.isArray(result?.data) && result?.data);

  return (
    <div className={`bg-white border border-slate-200 rounded-lg flex flex-col ${isKpi ? "col-span-1" : "col-span-1 md:col-span-2"}`} data-testid={`live-widget-${widget.id}`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b shrink-0">
        <div>
          <h3 className="text-sm font-medium text-slate-900">{widget.name}</h3>
          <code className="text-[10px] text-slate-400">{widget.collection || "audit_logs"}</code>
        </div>
        <div className="flex items-center gap-1">
          <Badge variant="outline" className="text-[10px]">{widget.type}</Badge>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onRefresh(widget)}>
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""} text-slate-400`} />
          </Button>
        </div>
      </div>
      <div className="p-3" style={{ height: isKpi ? "128px" : "224px" }}>
        {renderChart()}
      </div>
    </div>
  );
}

export default function LiveDashboard() {
  const [widgets, setWidgets] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [perWidgetLoading, setPerWidgetLoading] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);
  const [seeding, setSeeding] = useState(false);

  const executeWidget = useCallback(async (widget) => {
    try {
      const res = await widgetsApi.execute({
        collection: widget.collection || "audit_logs",
        pipeline_json: widget.pipeline_json || widget.sql_query || "",
        widget_type: widget.type,
        limit: 50,
      });
      return { widgetId: widget.id, result: res };
    } catch {
      return { widgetId: widget.id, result: { success: false, error: "Execution failed", data: [] } };
    }
  }, []);

  const loadDashboard = useCallback(async (widgetList) => {
    if (!widgetList?.length) return;
    setExecuting(true);
    const executions = await Promise.allSettled(widgetList.map(w => executeWidget(w)));
    const map = {};
    executions.forEach(e => {
      if (e.status === "fulfilled") map[e.value.widgetId] = e.value.result;
    });
    setResults(map);
    setLastRefreshed(new Date());
    setExecuting(false);
  }, [executeWidget]);

  const refreshSingle = useCallback(async (widget) => {
    setPerWidgetLoading(prev => ({ ...prev, [widget.id]: true }));
    const res = await executeWidget(widget);
    setResults(prev => ({ ...prev, [res.widgetId]: res.result }));
    setPerWidgetLoading(prev => ({ ...prev, [widget.id]: false }));
  }, [executeWidget]);

  useEffect(() => {
    (async () => {
      try {
        const list = await widgetsApi.list();
        const active = list.filter(w => w.status === "active");
        setWidgets(active);
        await loadDashboard(active);
      } catch {
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadDashboard]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => loadDashboard(widgets), 30000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, widgets, loadDashboard]);

  const handleSeedAndLoad = async () => {
    setSeeding(true);
    try {
      const res = await widgetsApi.seedDefaults();
      toast.success(res.message);
      const list = await widgetsApi.list();
      const active = list.filter(w => w.status === "active");
      setWidgets(active);
      await loadDashboard(active);
    } catch (e) {
      toast.error(e.message || "Failed to seed widgets");
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading live dashboard...
      </div>
    );
  }

  return (
    <div data-testid="live-dashboard-page">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="live-dashboard-title">Live Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Real-time metrics from your widget pipelines
            {lastRefreshed && <span className="ml-2 text-slate-400">· Last updated {lastRefreshed.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 border rounded-md px-3 py-1.5">
            <div className={`w-2 h-2 rounded-full ${autoRefresh ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
            <span className="text-xs text-slate-600">Auto-refresh 30s</span>
            <button
              className={`w-9 h-5 rounded-full transition-colors ${autoRefresh ? "bg-emerald-500" : "bg-slate-200"}`}
              onClick={() => setAutoRefresh(a => !a)}
            >
              <div className={`w-4 h-4 bg-white rounded-full shadow mx-0.5 transition-transform ${autoRefresh ? "translate-x-4" : ""}`} />
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadDashboard(widgets)} disabled={executing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${executing ? "animate-spin" : ""}`} />
            Refresh All
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/widgets"><LayoutGrid className="w-4 h-4 mr-2" />Manage Widgets</Link>
          </Button>
        </div>
      </div>

      {/* Summary bar */}
      {widgets.length > 0 && (
        <div className="flex items-center gap-4 mb-6 text-sm text-slate-500">
          <span className="flex items-center gap-1"><Activity className="w-4 h-4 text-blue-500" /> {widgets.length} active widget(s)</span>
          <span className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${executing ? "bg-amber-400 animate-pulse" : "bg-emerald-400"}`} />
            {executing ? "Running pipelines..." : "All pipelines up to date"}
          </span>
        </div>
      )}

      {/* Empty state */}
      {widgets.length === 0 && (
        <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-lg text-slate-400">
          <BarChart3 className="w-12 h-12 mx-auto mb-4" />
          <p className="text-base font-medium text-slate-600 mb-2">No active widgets to display</p>
          <p className="text-sm mb-6">Seed default widgets to see live metrics instantly, or create your own from the Widget Builder.</p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={handleSeedAndLoad} disabled={seeding}>
              {seeding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</> : <><Sparkles className="w-4 h-4 mr-2" /> Load Default Widgets</>}
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/widgets">Go to Widget Builder</Link>
            </Button>
          </div>
        </div>
      )}

      {/* Widget grid */}
      {widgets.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {widgets.map(w => (
            <WidgetCard
              key={w.id}
              widget={w}
              result={results[w.id]}
              loading={!!perWidgetLoading[w.id]}
              onRefresh={refreshSingle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
